import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import axios from 'axios';
import { EyeIcon, EyeSlashIcon, ShieldCheckIcon } from '@heroicons/react/24/outline';

import { API_BASE as API } from '../lib/config';

// Development mode check
const isDevelopment = process.env.NODE_ENV === 'development';

const AdminLogin = () => {
  const [formData, setFormData] = useState({
    adminId: '',
    password: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleDiagnose = async () => {
    setLoading(true);
    try {
      console.log('=== 管理者ログイン診断開始 ===');
      
      // 1. Backend URL確認
      console.log('API_BASE:', API);

// 1) 管理者初期化（存在確認も兼ねる）
await axios.post(`${API}/admin/init-account`, {}, { timeout: 10000 });

// 2) ログイン
const loginRes = await axios.post(`${API}/admin/auth/login`, {
  adminId: formData.adminId,
  password: formData.password,
});

// 3) 以後の全リクエストにトークンを付ける（これが重要）
const token = loginRes.data.token;
axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;

// 4) ME で検証（ヘッダー不要：上で共通化したため）
const meRes = await axios.get(`${API}/admin/auth/me`);

// 成功時に保存＆遷移
localStorage.setItem('admin_token', token);
localStorage.setItem('admin_user', JSON.stringify(meRes.data));
navigate('/admin/dashboard');

      
      // 2. 初期化API テスト
      try {
        const initResponse = await axios.post(`${API}/admin/init-account`, {}, { timeout: 10000 });
        console.log('初期化API結果:', initResponse.data);
        toast.info(`初期化API: ${initResponse.data.message}`);
      } catch (initError) {
        console.error('初期化API エラー:', initError);
        toast.error(`初期化API失敗: ${initError.message}`);
      }
      
      // 3. ログインAPI テスト（開発環境のみ、入力された資格情報を使用）
      if (isDevelopment && formData.adminId && formData.password) {
        try {
          const loginResponse = await axios.post(`${API}/admin/auth/login`, {
            admin_id: formData.adminId,
            password: formData.password
          }, { timeout: 10000 });

          console.log('ログインAPI結果:', loginResponse.data);
          toast.success('ログインAPI: 正常に動作しています');
        } catch (loginError) {
          console.error('ログインAPI エラー:', loginError);
          toast.error(`ログインAPI失敗: ${loginError.response?.data?.detail || loginError.message}`);
        }
      }
      
      console.log('=== 管理者ログイン診断完了 ===');
      
    } catch (error) {
      console.error('診断中にエラー:', error);
      toast.error('診断に失敗しました');
    } finally {
      setLoading(false);
    }
  };

  const handleInitializeAdmin = async () => {
    setLoading(true);
    try {
      const response = await axios.post(`${API}/admin/init-account`);
      
      if (response.data.success) {
        toast.success(response.data.message);
        console.log('Admin account initialized:', response.data);
      } else {
        toast.error(response.data.message);
      }
    } catch (error) {
      console.error('Admin initialization error:', error);
      toast.error('管理者アカウント初期化に失敗しました');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // ローカル判定
      const isLocal = (typeof window !== 'undefined' && window.location && window.location.host && window.location.host.includes('localhost:3000')) || (typeof API === 'string' && API.includes('localhost'));

      // 1) 常に初期化を先に試行（存在すれば no-op）
      try {
        const initResponse = await axios.post(`${API}/admin/init-account`, {}, { timeout: 10000 });
        console.log('Admin account initialization response:', initResponse.data);
      } catch (initErr) {
        console.warn('Init account request failed (continuing):', initErr?.response?.data || initErr.message);
      }

      // 2) 入力された資格情報で API ログインを試行
      try {
        const loginResponse = await axios.post(`${API}/admin/auth/login`, {
          admin_id: formData.adminId,
          password: formData.password
        }, { timeout: 10000 });

        const { access_token, admin } = loginResponse.data;

        localStorage.setItem('admin_token', access_token);
        localStorage.setItem('admin_user', JSON.stringify(admin));

        toast.success(`${admin.name}でログインしました`);
        window.location.href = '/admin-dashboard';
        return;
      } catch (apiError) {
        console.error('Admin API login failed:', apiError?.response?.data || apiError.message);

        // 3) ローカル or API不達の場合のデモフォールバック（開発環境のみ）
        const isDefaultCreds = isDevelopment && formData.adminId === 'work@turtlefit.jp' && formData.password === 'Kabuki23Kameki23';
        if (isDefaultCreds && (isLocal || !apiError?.response || [404, 502].includes(apiError?.response?.status))) {
          const demoAdmin = {
            id: 'local-admin-001',
            admin_id: 'work@turtlefit.jp',
            name: 'ローカル管理者',
            role: 'admin',
            permissions: ['all']
          };
          const demoToken = 'local_admin_token_' + Date.now();
          localStorage.setItem('admin_token', demoToken);
          localStorage.setItem('admin_user', JSON.stringify(demoAdmin));
          toast.success(`${demoAdmin.name}でログインしました（ローカル）`);
          window.location.href = '/admin/dashboard';
          return;
        }

        // 4) デフォルト資格情報での入力時のみデプロイモードにフォールバック（従来）
        if (isDefaultCreds) {
          console.log('Falling back to demo login for deployment...');
          const demoAdmin = {
            id: 'deploy-admin-001',
            admin_id: 'work@turtlefit.jp',
            name: 'デプロイ環境管理者',
            role: 'admin',
            permissions: ['all']
          };
          const demoToken = 'deploy_admin_token_' + Date.now();
          localStorage.setItem('admin_token', demoToken);
          localStorage.setItem('admin_user', JSON.stringify(demoAdmin));
          localStorage.setItem('deploy_admin_mode', 'true');
          toast.success(`${demoAdmin.name}でログインしました（デプロイモード）`);
          window.location.href = '/admin/dashboard';
          return;
        }

        // 5) それ以外は明示エラー
        toast.error('管理者IDまたはパスワードが正しくありません');
      }

    } catch (error) {
      console.error('Admin login error:', error);
      toast.error('ログインに失敗しました。管理者アカウント初期化をお試しください。');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="text-center">
          <div className="mx-auto w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center mb-4">
            <ShieldCheckIcon className="w-8 h-8 text-white" />
          </div>
          <h2 className="text-3xl font-bold text-gray-900">バイターズ</h2>
          <p className="mt-2 text-sm text-gray-600">運営管理システム</p>
        </div>
        <h2 className="mt-6 text-center text-2xl font-extrabold text-gray-900">
          管理者ログイン
        </h2>
        <p className="mt-2 text-center text-sm text-gray-600">
          運営チーム専用のアクセスです
        </p>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-8 px-4 shadow-xl sm:rounded-lg sm:px-10 border border-gray-200">
          <form className="space-y-6" onSubmit={handleSubmit}>
            <div>
              <label htmlFor="adminId" className="block text-sm font-medium text-gray-700">
                管理者ID
              </label>
              <input
                id="adminId"
                name="adminId"
                type="text"
                required
                className="mt-1 appearance-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-lg focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm"
                value={formData.adminId}
                onChange={handleChange}
                placeholder="管理者IDを入力"
              />
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                パスワード
              </label>
              <div className="mt-1 relative">
                <input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  required
                  className="appearance-none relative block w-full px-3 py-2 pr-10 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-lg focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm"
                  value={formData.password}
                  onChange={handleChange}
                  placeholder="パスワードを入力"
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? (
                    <EyeSlashIcon className="h-5 w-5 text-gray-400" />
                  ) : (
                    <EyeIcon className="h-5 w-5 text-gray-400" />
                  )}
                </button>
              </div>
            </div>

            <div>
              <button
                type="submit"
                disabled={loading}
                className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-lg text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {loading ? (
                  <div className="flex items-center">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    ログイン中...
                  </div>
                ) : (
                  'ログイン'
                )}
              </button>
            </div>
            
            {/* 管理者アカウント初期化ボタン（デプロイ環境用） */}
            <div className="mt-4 space-y-2">
              <button
                type="button"
                onClick={handleInitializeAdmin}
                disabled={loading}
                className="w-full flex justify-center py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                管理者アカウントを初期化
              </button>
              
              <button
                type="button"
                onClick={handleDiagnose}
                disabled={loading}
                className="w-full flex justify-center py-2 px-4 border border-orange-300 rounded-md shadow-sm text-sm font-medium text-orange-700 bg-orange-50 hover:bg-orange-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                ログイン診断を実行
              </button>
              
              <p className="mt-1 text-xs text-gray-500 text-center">
                デプロイ環境でログインできない場合に使用
              </p>
            </div>
          </form>
        </div>
      </div>

      {/* フッター */}
      <div className="mt-8 text-center">
        <p className="text-xs text-gray-500">
           2024 バイターズ運営チーム. All rights reserved.
        </p>
      </div>
    </div>
  );
};

export default AdminLogin;